/**
 * Los `error_kind` de una cuenta publicitaria caída, y el único resolver que los traduce.
 *
 * REGLA DE LA CASA: una clave que viene del backend NUNCA indexa un catálogo (ni un archivo de
 * i18n) sin fallback. Un kind nuevo tiene que DEGRADAR a `GENERIC`, no imprimir la key cruda ni
 * afirmar algo que no sabemos. Afirmar "la cuenta está deshabilitada" sin confirmación de la
 * plataforma es el falso positivo que motivó `KNOWN_ERROR_KINDS` en su momento.
 *
 * Vive acá y no en un componente porque el mismo enum lo consumen cuatro superficies: el tooltip
 * del Tablero (`dashboard/rows/BusinessUnitAdAccountNameCell`), el modal de reconexión
 * (`agency/connections/PaidMediaReconnectFlow`), la fila del Listado v2 (`agency/v2/ClientV2Row`)
 * y la ficha de cuentas del cliente. Un dato, un dibujo.
 */

export const ERROR_KIND_PERMISSIONS = 'PERMISSIONS';
export const ERROR_KIND_CHANNEL_DISCONNECTED = 'CHANNEL_DISCONNECTED';
export const ERROR_KIND_GA4_PROPERTY_DENIED = 'GA4_PROPERTY_DENIED';
export const ERROR_KIND_FB_SECURITY_CHECK = 'FB_SECURITY_CHECK';
export const ERROR_KIND_ACCOUNT_DISABLED = 'ACCOUNT_DISABLED';

/**
 * El canal SHEET no tiene OAuth ni plataforma de anuncios que revisar: la cuenta se cayó porque la
 * PLANILLA tiene datos que no se pueden interpretar (una fecha con otro formato, un costo con
 * letras). Sin este kind el copy caía en el genérico, que manda a "revisar la conexión con la
 * plataforma" — el lugar equivocado.
 */
export const ERROR_KIND_SHEET_DATA_INVALID = 'SHEET_DATA_INVALID';

/**
 * El otro modo de falla del canal SHEET, y el más frecuente: la planilla YA NO SE PUEDE LEER. O el
 * Google Sheet dejó de estar publicado como CSV, o el archivo se borró/movió, o su mapeo de columnas
 * y filtros guardados ya no coinciden con el archivo. Medido en producción el 2026-09-01: de 28
 * cuentas SHEET caídas, 26 son éste (las otras 2 son `SHEET_DATA_INVALID`).
 *
 * Se separa de `SHEET_DATA_INVALID` porque la acción es otra: ahí el archivo se lee y hay filas con
 * valores raros; acá no llegamos ni a leerlo. El diagnóstico del modal "Editar conexión con el
 * Sheet/CSV" ya distingue cuál de las tres causas es (`sheet_account.diagnostic.title_url` /
 * `title_mapping` / `title_filters`), así que el copy manda ahí en vez de adivinar.
 */
export const ERROR_KIND_SHEET_NOT_READABLE = 'SHEET_NOT_READABLE';

export const ERROR_KIND_GENERIC = 'GENERIC';

export const KNOWN_ERROR_KINDS = [
    ERROR_KIND_PERMISSIONS,
    ERROR_KIND_CHANNEL_DISCONNECTED,
    ERROR_KIND_GA4_PROPERTY_DENIED,
    ERROR_KIND_FB_SECURITY_CHECK,
    ERROR_KIND_ACCOUNT_DISABLED,
    ERROR_KIND_SHEET_DATA_INVALID,
    ERROR_KIND_SHEET_NOT_READABLE,
    ERROR_KIND_GENERIC,
];

/** Los kinds del canal SHEET: ninguno se arregla reconectando. */
const SPREADSHEET_ERROR_KINDS = [
    ERROR_KIND_SHEET_DATA_INVALID,
    ERROR_KIND_SHEET_NOT_READABLE,
];

/** Devuelve SIEMPRE un kind con copy propio. Nunca `undefined`, nunca la clave cruda del backend. */
export function resolveErrorKind(errorKind) {
    return KNOWN_ERROR_KINDS.includes(errorKind) ? errorKind : ERROR_KIND_GENERIC;
}

/**
 * ¿El problema se arregla en la PLANILLA (o en la conexión con ella) en vez de reconectando?
 * Vale para los DOS kinds de sheet: `SHEET_DATA_INVALID` y `SHEET_NOT_READABLE`.
 *
 * Existe porque SHEET está tipado como paid media (`channels.js`), así que los gates de reconexión
 * (`BusinessUnitAdAccountNameCell._canReconnect`, `AccountRows.supportsReconnect`) lo dan por bueno
 * y ofrecen "Reconectar" — pero `PaidMediaReconnectFlow.handleConnectNew()` no tiene rama SHEET y
 * el botón no hace NADA. Ese botón muerto es PREEXISTENTE y no se toca acá; lo que este helper
 * evita es que los kinds de sheet lo hereden, que sería empeorarlo: ni para un dato mal formado ni
 * para una hoja que dejó de publicarse el OAuth es la acción correcta — la primera se arregla en la
 * planilla y la segunda volviendo a publicarla o corrigiendo la conexión desde su propio modal.
 */
export function isFixedInTheSpreadsheet(errorKind) {
    return SPREADSHEET_ERROR_KINDS.includes(resolveErrorKind(errorKind));
}
